import { hasReport } from '@glass/types';
import { useCallback, useState } from 'react';
import { Linking, Pressable, ScrollView, Text, View } from 'react-native';
import { DecisionPill, DOCUMENT, Glyph, ReviewerLine } from '~/components/decision';
import { Screen } from '~/components/Screen';
import { BackHeader, Card, Eyebrow, INFO, Notice, PrimaryButton } from '~/components/ui';
import { useJob } from '~/hooks/useJob';
import { ApiError, api } from '~/lib/api';
import { areaTitle } from '~/lib/areas';
import { logger } from '~/lib/logger';
import { useTheme } from '~/theme/ThemeProvider';

export interface VerdictScreenProps {
  jobId: string;
  onBack: () => void;
  /** Where back goes, in the header. From a submit that is the journeys list. */
  backLabel?: string;
}

/**
 * The verdict, as a REPORT rather than an approval queue.
 *
 * A verdict that reaches this screen is terminal and cannot be changed from here. Review
 * happens BEFORE release, in the desk console, which is why there is a reviewer line and
 * still no override, no appeal and no annotation — and none should be added. By the time
 * an operator reads this, the decision is settled.
 *
 * Per-leg model rows stay off the default surface: a customer shown three models
 * disagreeing reads it as "nobody knows". Evidence lives behind the findings affordance.
 */
export function VerdictScreen({
  jobId,
  onBack,
  backLabel = 'Recent journeys',
}: VerdictScreenProps) {
  const { c, t, radius, space, touch } = useTheme();
  const { job, error } = useJob(jobId);
  const result = job?.result;

  if (error && !job) {
    return (
      <Screen>
        <BackHeader title={backLabel} onBack={onBack} />
        <View style={{ padding: space.md, gap: space.md }}>
          <Notice tone="stateFailed" title="Could not load this verdict.">
            {error}
          </Notice>
        </View>
      </Screen>
    );
  }

  const v = result?.enrichment?.vin?.vehicle;
  const vehicle = v ? `${v.make} ${v.model}` : undefined;
  const decision = result?.decision;
  /*
    A verdict on this screen is terminal and released — D19 holds every job for review
    before release, and nothing reaches an operator until it has been. So the decision is
    the whole question, and R2 answers it.
  */
  const reportable = hasReport(decision, Boolean(result));
  const [reportGone, setReportGone] = useState(false);
  const [reportNote, setReportNote] = useState<string | null>(null);

  const openReport = useCallback(async () => {
    setReportNote(null);
    try {
      const { url } = await api.reportLink(jobId);
      /*
        The SIGNED url, not the stable one. Opening a link carries no Authorization header
        — on web or through the system browser — so the stable link would arrive as a 401.
      */
      await Linking.openURL(url);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ERR_NO_REPORT') {
        // R2 says this job never gets one. Our own rule disagreed, so stop offering it.
        setReportGone(true);
        return;
      }
      if (e instanceof ApiError && e.code === 'ERR_REPORT_NOT_READY') {
        // Owed, not produced yet. The operator can simply tap again.
        setReportNote('Still being prepared. Try again in a moment.');
        return;
      }
      // The CODE, not the status: a 404 here is also what "no such job" looks like.
      logger.error('[Verdict] report link failed', e);
      setReportNote('Could not open the report.');
    }
  }, [jobId]);
  const unable = decision === 'unable_to_assess';
  const insufficient = decision === 'insufficient_evidence';

  return (
    <Screen>
      <BackHeader title={backLabel} onBack={onBack} />

      <ScrollView contentContainerStyle={{ padding: space.md, gap: space.md }}>
        {!result ? (
          <Text style={[t.body, { color: c.dim }]}>Waiting for the verdict…</Text>
        ) : (
          <>
            <View
              style={{
                borderRadius: radius.lg,
                padding: space.md,
                backgroundColor: c.surface,
                borderWidth: 1,
                borderColor: c.border,
                gap: space.sm,
              }}
            >
              {/* The frame's own heading for this card: what it is, and whose glass. */}
              <Eyebrow>{vehicle ? `Verdict · ${vehicle}` : 'Verdict'}</Eyebrow>

              {decision ? <DecisionPill decision={decision} testID="decision-word" /> : null}

              {/*
                Who settled it. One quiet line, never the reviewer's name and never what
                they changed — the operator cannot act on either, and both belong to the
                reviewer console's audit trail.
              */}
              <ReviewerLine source={result.decision_source} />

              {/*
                unable_to_assess must never be mistaken for "no damage found". The pill is
                tinted like every other decision, so the distinction is carried in words.
              */}
              {unable ? (
                <Text style={[t.bodyLg, { color: c.text, fontWeight: '600' }]}>
                  This is not a finding of no damage. The photographs were usable, but no defensible
                  conclusion could be reached.
                </Text>
              ) : null}

              <Text style={[t.bodyLg, { color: c.text }]} testID="justification">
                {result.justification}
              </Text>

              {job?.status === 'partial' ? (
                <Notice title="One check did not complete.">The verdict stands on the rest.</Notice>
              ) : null}
            </View>

            {unable ? (
              <Card style={{ gap: space.xs }}>
                <Eyebrow>what to do</Eyebrow>
                <Text style={[t.body, { color: c.text }]}>
                  Inspect in person before the vehicle is driven. Fresh photographs from a different
                  angle may resolve it, but are not guaranteed to.
                </Text>
              </Card>
            ) : null}

            {insufficient && result.blocking_reasons.length > 0 ? (
              <Card style={{ gap: space.xs }}>
                <Eyebrow>why</Eyebrow>
                {result.blocking_reasons.map((r) => (
                  <Text key={r} style={[t.body, { color: c.text }]}>
                    · {r.replace(/_/g, ' ')}
                  </Text>
                ))}
              </Card>
            ) : null}

            {/*
              One card, one row per area — the frame's shape. They were separate cards
              under a "per area" eyebrow, which read as four verdicts rather than one
              verdict and its parts.
            */}
            <Card style={{ gap: space.md }}>
              {result.areas.map((a) => (
                <View key={a.glass_area} style={{ gap: space.xs }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: space.sm,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Text style={[t.bodyLg, { fontWeight: '600', color: c.text, flex: 1 }]}>
                      {areaTitle(a.glass_area)}
                    </Text>
                    <DecisionPill decision={a.decision} testID={`area-${a.glass_area}`} />
                  </View>
                  {a.judge_agreement < 1 ? (
                    <Text style={[t.body, { color: c.dim }]}>
                      Borderline reading — re-checked before the verdict was issued.
                    </Text>
                  ) : null}
                </View>
              ))}
            </Card>

            {result.enrichment?.vin?.adas_present && decision === 'replace' ? (
              // A cost and a safety step, not a caveat about the reading — so it is a
              // named surface with its own glyph rather than a card with a tinted border.
              <Notice tone="stateInfo" glyph={INFO} title="ADAS recalibration required.">
                Driver-assistance cameras are mounted to this glass and must be recalibrated after
                replacement.
              </Notice>
            ) : null}

            {/*
              The assessment report, when this job has one (21.4, R2).

              A row rather than a button in the footer: it is a thing to read, not the way
              out of the screen — "Done" keeps that job. Opening it leaves the app, on web
              to a new tab and on the device to the system browser, because a PDF is not
              something this app renders.

              Hidden entirely when no report is owed. The endpoint answers 409
              ERR_NO_REPORT for a decision that never gets one, and finding that out by
              offering the row and letting an operator press it is the wrong way round.
            */}
            {reportable && !reportGone ? (
              <Card>
                <Pressable
                  testID="open-report"
                  accessibilityRole="link"
                  accessibilityLabel="Open the assessment report"
                  onPress={() => void openReport()}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: space.sm,
                    minHeight: touch.min,
                  }}
                >
                  <Glyph path={DOCUMENT} size={20} color={c.text} />
                  <View style={{ flex: 1 }}>
                    <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>Report</Text>
                    <Text style={[t.body, { color: c.dim }]}>
                      {reportNote ?? 'The assessment as a PDF, to send or to file.'}
                    </Text>
                  </View>
                  <Text style={[t.bodyLg, { color: c.dim }]}>›</Text>
                </Pressable>
              </Card>
            ) : null}
          </>
        )}
      </ScrollView>

      <View style={{ paddingHorizontal: space.md, paddingBottom: space.md }}>
        <PrimaryButton testID="verdict-done" label="Done" onPress={onBack} />
      </View>
    </Screen>
  );
}
